import React from 'react';
import { useTranslation } from 'react-i18next';
import SupportForm from '../../components/user/SupportForm';

const MapPinIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
);

const EnvelopeIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg>
);

const PhoneIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 6.75Z" /></svg>
);

export default function ContactPage() {
  const { t } = useTranslation();
  document.title = t('contact_page.page_title');

  return (
    <>
      <div className="bg-white">
        <div
          className="relative w-full h-[50vh] min-h-[350px] bg-cover bg-center"
          style={{ backgroundImage: `url(/images/background/LienHe.png)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <div className="relative z-10 flex flex-col items-center justify-center h-full px-6 text-white text-center">
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4">
              {t('contact_page.hero.title')}
            </h1>
            <p className="max-w-2xl text-md md:text-xl leading-relaxed">
              {t('contact_page.hero.subtitle')}
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto py-16 sm:py-24 px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                <div className="space-y-8 text-gray-800">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900">{t('contact_page.info.title')}</h2>
                        <p className="mt-3 text-lg text-gray-600">{t('contact_page.info.subtitle')}</p>
                    </div>

                    <div className="space-y-6">
                        <div className="flex items-start gap-4">
                            <MapPinIcon className="h-6 w-6 text-green-700 mt-1 flex-shrink-0" />
                            <div>
                                <h3 className="font-semibold">{t('contact_page.info.address.title')}</h3>
                                <p className="text-gray-600"><strong>{t('footer.contact.headquarters_title')}:</strong> {t('footer.contact.headquarters_address')}</p>
                                <p className="text-gray-600"><strong>{t('footer.contact.factory_title')}:</strong> {t('footer.contact.factory_address')}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4">
                            <EnvelopeIcon className="h-6 w-6 text-green-700 mt-1 flex-shrink-0" />
                            <div>
                                <h3 className="font-semibold">{t('contact_page.info.email.title')}</h3>
                                <a href="mailto:ctg@cleantogreentcg.com" className="text-gray-600 hover:text-green-700 hover:underline">
                                ctg@cleantogreentcg.com
                                </a>
                            </div>
                        </div>

                        <div className="flex items-start gap-4">
                            <PhoneIcon className="h-6 w-6 text-green-700 mt-1 flex-shrink-0" />
                            <div>
                                <h3 className="font-semibold">{t('contact_page.info.phone.title')}</h3>
                                <p className="text-gray-600">
                                <a href="tel:+84867003331" className="hover:text-green-700">(+84) 867 003 331</a>
                                <span> - </span>
                                <a href="tel:+84867003337" className="hover:text-green-700">(+84) 867 003 337</a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full h-[450px] lg:h-full overflow-hidden shadow-lg">
                    <iframe
                        title={t('contact_page.map.title')}
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3919.954490333555!2d106.60205847588303!3d10.738010059885817!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31752c06a9284985%3A0xb6337839c4a43a84!2zQ8O0bmcgVHkgVE5ISCBT4bqjbiBYdeG6pXQgVGjGsMahbmcgTeG6oWkgS-G7uSBUaHXhuq10IENURw!5e0!3m2!1svi!2s!4v1723623912187!5m2!1svi!2s"
                        width="100%"
                        height="100%"
                        className="border-0"
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                </div>
            </div>
        </div>
      </div>
      <SupportForm />
    </>
  );
}